import { useMemo } from 'react';

import { NumberCoercible } from '../../../utils/NumberCoercible';
import { NumberScale } from '../../../utils/NumberScale';
import { TimeScale } from '../../../utils/TimeScale';
import { ShapeGenerator } from '../ShapeGenerator';
import { ShapeGeneratorFactory } from '../ShapeGeneratorFactory';

interface UseShapeGeneratorParams<TPointType> {
  /**
   * A factory function that returns a shape generator.
   */
  shapeGeneratorFactory: ShapeGeneratorFactory<TPointType>;
  /**
   * The min of X domain.
   */
  xDomainMin: NumberCoercible | undefined;
  /**
   * The max of X domain.
   */
  xDomainMax: NumberCoercible | undefined;
  /**
   * The x-scale used for display.
   */
  xScale: TimeScale;
  /**
   * The min of y domain.
   */
  yDomainMin: NumberCoercible | undefined;
  /**
   * The max of y domain.
   */
  yDomainMax: NumberCoercible | undefined;
  /**
   * The y-scale used for display.
   */
  yScale: NumberScale;
}

export const useShapeGenerator = <TPointType,>({
  xDomainMin,
  xDomainMax,
  xScale,
  yDomainMin,
  yDomainMax,
  yScale,
  shapeGeneratorFactory,
}: UseShapeGeneratorParams<TPointType>): ShapeGenerator<TPointType> => {
  const shapeGenerator = useMemo(() => {
    return shapeGeneratorFactory({ xScale, yScale, xDomainMin, xDomainMax, yDomainMin, yDomainMax });
  }, [xScale, xDomainMin, xDomainMax, yScale, yDomainMin, yDomainMax, shapeGeneratorFactory]);

  return shapeGenerator;
};
